import React, { useRef, useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import { ListGroup } from 'react-bootstrap';
import { useDrag, useDrop } from 'react-dnd';
import { List } from 'immutable';
import uuid from 'uuid';
import { HIDDEN_ENTITY_TYPES } from 'src/apps/generic/Utils';

function Item({ scope, payload, index, isVisible, children, handlePositionChange }) {
  const ref = useRef(null);
  const [{ isDragging }, drag] = useDrag({
    type: scope,
    item: () => ({ payload, index, isVisible }),
    collect: (monitor) => ({
      isDragging: !!monitor.isDragging(),
    })
  });

  const [{ isOver }, drop] = useDrop({
    accept: scope,
    collect: (monitor) => ({
      isOver: !!monitor.isOver()
    }),
    hover(item) {
      if (!ref.current) return;

      const {
        payload: dragPayload,
        index: dragIndex,
        isVisible: dragIsVisible
      } = item

      // Don't replace items with themselves
      if (dragIndex === index && dragIsVisible === isVisible) return;

      handlePositionChange(dragPayload, dragIndex, dragIsVisible, index, isVisible);

      item.index = index;
      item.isVisible = isVisible;
    },
  });

  const className = (() => {
    let nameString = "tab-layout-editor-list-item";
    if (isDragging) {
      nameString += " active";
    }
    if (HIDDEN_ENTITY_TYPES.includes(payload)) {
      nameString += ' jiuzhou-hidden';
    }
    return nameString;
  })();

  drag(drop(ref));
  return (
    <ListGroup.Item ref={ref} className={className} data-item={payload}>
      {children}
    </ListGroup.Item>
  );
}

export default function TabLayoutEditor({
  visible,
  hidden,
  getItemKey,
  getItemComponent,
  onLayoutChange,
}) {
  const visibleRef = useRef(null);
  const hiddenRef = useRef(null);
  const [ scope ] = useState(uuid.v4());

  const [{ isOver: isOverVisible }, dropVisible] = useDrop({
    accept: scope,
    collect: (monitor) => ({
      isOver: !!monitor.isOver({ shallow: true })
    }),
    hover(item, monitor) {
      if (!visibleRef.current) return;

      const visibleNonHidden = visible.filter(
        (v) => !HIDDEN_ENTITY_TYPES.includes(v)
      );
      if (visibleNonHidden.size > 0) return;

      if (monitor.isOver({ shallow: true })) {
        const { index: oldIndex, payload } = item;
        onLayoutChange(visible.insert(0, payload), hidden.delete(oldIndex));

        item.index = 0;
        item.isVisible = true;
      }
    }
  });
  dropVisible(visibleRef);

  const [{ isOver: isOverHidden }, dropHidden] = useDrop({
    accept: scope,
    collect: (monitor) => ({
      isOver: !!monitor.isOver({ shallow: true })
    }),
    hover(item, monitor) {
      if (!visibleRef.current) return;

      const hiddenNonHidden = hidden.filter(
        (h) => !HIDDEN_ENTITY_TYPES.includes(h)
      );
      if (hiddenNonHidden.size > 0) return;

      const { index: oldIndex, payload } = item;
      if (monitor.isOver({ shallow: true })) {
        onLayoutChange(visible.delete(oldIndex), hidden.insert(0, payload));

        item.index = 0;
        item.isVisible = false;
      }
    }
  });
  dropHidden(hiddenRef);

  const handlePositionChange = useCallback((
    item,
    oldIndex,
    oldIsVisible,
    newIndex,
    newIsVisible
  ) => {
    if (oldIsVisible == newIsVisible) {
      // Moving item within the same list
      if (oldIsVisible) {
        // Moving item within the visible list
        onLayoutChange(
          visible.delete(oldIndex).insert(newIndex, item),
          hidden
        );
      } else {
        // Moving item within the hidden list
        onLayoutChange(
          visible,
          hidden.delete(oldIndex).insert(newIndex, item)
        );
      }
    } else if (oldIsVisible) {
      // Moving item from visible to hidden
      onLayoutChange(visible.delete(oldIndex), hidden.insert(newIndex, item));
    } else {
      // Moving item from hidden to visible
      onLayoutChange(visible.insert(newIndex, item), hidden.delete(oldIndex));
    }
  }, [visible, hidden]);

  const lists = [
    {
      title: 'Visible',
      isVisible: true,
      collection: visible,
      isOver: isOverVisible,
      ref: visibleRef
    },
    {
      title: 'Hidden',
      isVisible: false,
      collection: hidden,
      isOver: isOverHidden,
      ref: hiddenRef
    },
  ]

  return (
    <div>
      {lists.map(({ title, isVisible, collection, isOver, ref }) => {
        const isEmpty = collection.size === 0;
        const listClassName = [
          'my-1 tab-layout-editor-list',
          isEmpty && 'jiuzhou-empty',
          isOver && 'jiuzhou-is-over',
        ].filter(Boolean).join(' ');
        return (
          <div
            key={title}
            ref={ref}
            onMouseDown={(e) => e.preventDefault()}
          >
            <h5>{title}</h5>
            <ListGroup className={listClassName} variant="flush">
              {collection.map((item, index) => (
                <Item
                  scope={scope}
                  key={getItemKey(item)}
                  payload={item}
                  isVisible={isVisible}
                  index={index}
                  handlePositionChange={handlePositionChange}
                >
                  {getItemComponent({ item, isVisible })}
                </Item>
              ))}
              {isEmpty && (
                <ListGroup.Item className="jiuzhou-empty-placeholder">
                  {isOver ? 'Release to drop' : `Drag items here to mark as ${title.toLowerCase()}`}
                </ListGroup.Item>
              )}
            </ListGroup>
          </div>
        );
      })}
    </div>
  );
}

TabLayoutEditor.propTypes = {
  visible: PropTypes.instanceOf(List).isRequired,
  hidden: PropTypes.instanceOf(List).isRequired,
  onLayoutChange: PropTypes.func.isRequired,
  getItemKey: PropTypes.func,
  getItemComponent: PropTypes.func,
}

TabLayoutEditor.defaultProps = {
  getItemKey: (item) => item,
  gitItemComponent: ({ item }) => item,
}
